"use client";

import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function VisionaryArchitecture() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const services = [{
    id: "01",
    title: "Architecture",
    description: "Innovative structural design and planning."
  }, {
    id: "02",
    title: "Interior Design",
    description: "Curated spaces that balance form and function."
  }, {
    id: "03",
    title: "Construction",
    description: "Precision engineering and high-quality execution."
  }, {
    id: "04",
    title: "Project Management",
    description: "Full-cycle oversight from concept to completion."
  }];
  return <section ref={sectionRef} data-section-id="232100" id="visionary-architecture" className="relative bg-[#1A1A1A] py-24 md:py-32 overflow-hidden">
      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes typing {
          from { width: 0 }
          to { width: 100% }
        }
        .typing-animation {
          overflow: hidden;
          white-space: nowrap;
          display: inline-block;
          width: 0;
        }
        .animate-typing {
          animation: typing 3.5s steps(40, end) forwards;
        }
      `
    }} />

      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <div className="absolute inset-0" style={{
        backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 items-center">
          
          <div className={`flex-1 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-[10px] font-bold tracking-[0.3em] text-[#FF4500] uppercase">
                Established 2007
              </span>
              <div className="h-px w-8 bg-[#FF4500]/30" />
            </div>

            <h2 className="font-sans text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-8 tracking-tight">
              <span className={`typing-animation ${isVisible ? "animate-typing" : ""}`} style={{
              textShadow: '2px 2px 4px rgba(0,0,0,0.05)'
            }}>
                Crafting Spaces with <br />
                <span className="text-[#FF4500]">Architectural Precision</span>
              </span>
            </h2>

            <p className="font-sans text-lg text-white/70 leading-relaxed mb-10 max-w-xl">
              ArchiForma delivers full-turnkey solutions for residential and commercial projects. 
              We combine creative vision with technical expertise to create modern, functional, 
              and timeless environments that reflect our clients' unique aspirations.
            </p>

            <div className="flex flex-wrap gap-4 mb-16">
              <Button asChild className="bg-[#FF4500] hover:bg-[#FF4500]/90 text-white rounded-md px-8 py-6 h-auto text-sm font-bold uppercase tracking-widest shadow-md transition-transform hover:-translate-y-1">
                <Link to="/HomePage">
                  View Portfolio
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-white/30 text-black hover:bg-white/10 rounded-md px-8 py-6 h-auto text-sm font-bold uppercase tracking-widest transition-all">
                <Link to="/HomePage">
                  Our Process
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 border-t border-white/20 pt-12">
              {services.map((service, i) => <div key={service.id} data-index={i} className="group">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-mono text-[10px] text-[#FF4500] font-bold">
                      {service.id}
                    </span>
                    <h3 className="font-sans text-sm font-bold uppercase tracking-wider text-white">
                      {service.title}
                    </h3>
                  </div>
                  <p className="font-sans text-xs text-white/60 leading-normal group-hover:text-white transition-colors">
                    {service.description}
                  </p>
                </div>)}
            </div>
          </div>

          <div className={`flex-1 relative transition-all duration-1000 delay-300 ease-out ${isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t border-l border-[#FF4500]/40 pointer-events-none" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b border-r border-[#FF4500]/40 pointer-events-none" />
              
              <div className="relative overflow-hidden rounded-md shadow-lg bg-[#F8F8F8]">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/modern_architectural_structure.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/modern_architectural_structure.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689812/img/modern_architectural_structure.png-wvc-sizes" alt="Modern architectural structure showing precision design" loading="lazy" className="w-full h-[500px] lg:h-[600px] object-cover grayscale hover:grayscale-0 transition-all duration-700" />
                
                <div className="absolute bottom-8 right-8 bg-white p-6 rounded-md shadow-lg border border-[#E5E5E5] max-w-[200px] hidden md:block">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] text-[#FF4500] font-bold uppercase tracking-tighter">Quality Index</span>
                      <span className="font-mono text-[10px] text-[#1A1A1A] font-bold">98%</span>
                    </div>
                    <div className="h-1 w-full bg-[#F8F8F8] rounded-lg overflow-hidden">
                      <div className="h-full bg-[#FF4500] w-[98%]" />
                    </div>
                    <p className="font-sans text-[10px] text-[#1A1A1A]/60 leading-tight mt-1">
                      Exceeding industry standards in structural integrity and design execution.
                    </p>
                  </div>
                </div>
              </div>

              <div className="absolute -top-8 -right-8 bg-[#1A1A1A] text-white p-8 rounded-md shadow-lg hidden lg:flex flex-col items-center justify-center min-w-[140px]">
                <span className="font-sans text-4xl font-bold leading-none text-[#FF4500]">15+</span>
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] mt-2 text-white/70 text-center">
                  Years of <br /> Excellence
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}